import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface OnboardingStore {
  isOnboardingOpen: boolean;
  hasCompletedOnboarding: boolean;
  currentStep: number; // 0-2

  // Actions
  showOnboarding: () => void;
  hideOnboarding: () => void;
  completeOnboarding: () => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  resetOnboarding: () => void; // For testing
}

export const ONBOARDING_TOTAL_STEPS = 3;

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      isOnboardingOpen: false,
      hasCompletedOnboarding: false,
      currentStep: 0,

      showOnboarding: () => set({ isOnboardingOpen: true, currentStep: 0 }),
      hideOnboarding: () => set({ isOnboardingOpen: false }),
      completeOnboarding: () => set({ isOnboardingOpen: false, hasCompletedOnboarding: true }),
      setStep: (step: number) => set({ currentStep: Math.max(0, Math.min(step, ONBOARDING_TOTAL_STEPS - 1)) }),
      nextStep: () => set({ currentStep: Math.min(get().currentStep + 1, ONBOARDING_TOTAL_STEPS - 1) }),
      prevStep: () => set({ currentStep: Math.max(get().currentStep - 1, 0) }),
      resetOnboarding: () => set({ hasCompletedOnboarding: false, currentStep: 0 }),
    }),
    {
      name: 'crowdfpl-onboarding',
      partialize: (state) => ({ hasCompletedOnboarding: state.hasCompletedOnboarding }),
    },
  ),
);

/**
 * Convenience hook with shorter aliases
 */
export function useOnboarding() {
  const store = useOnboardingStore();
  return {
    isOpen: store.isOnboardingOpen,
    hasCompleted: store.hasCompletedOnboarding,
    currentStep: store.currentStep,
    show: store.showOnboarding,
    hide: store.hideOnboarding,
    complete: store.completeOnboarding,
    setStep: store.setStep,
    nextStep: store.nextStep,
    prevStep: store.prevStep,
    reset: store.resetOnboarding,
  };
}
